import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  HeartPulse,
  Cpu,
  MemoryStick,
  Activity,
  Database,
  Pause,
  Play,
  Download,
  AlertTriangle,
  Clock,
  RefreshCw,
  CheckCircle2,
  HardDrive,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";

const POLL_OPTIONS = [
  { value: 5, label: "5s" },
  { value: 10, label: "10s" },
  { value: 30, label: "30s" },
  { value: 60, label: "60s" },
];
const BUFFER_MAX = 60; // max 60 punti (30 min a 30s)
const STRESS_WARN = 3.0;
const STRESS_CRIT = 5.0;
const CONSECUTIVE_ERR_THRESHOLD = 3;

/* ---------- Helpers --------------------------------------------------- */
function pctColor(v, { greenMax, amberMax }) {
  if (v == null || Number.isNaN(v)) return "text-muted-foreground";
  if (v < greenMax) return "text-emerald-500";
  if (v < amberMax) return "text-amber-500";
  return "text-rose-500";
}
function fmtRelative(iso) {
  if (!iso || iso === "None") return "—";
  try {
    const t = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.round((t - now) / 1000);
    if (!Number.isFinite(diff)) return "—";
    const abs = Math.abs(diff);
    const prefix = diff >= 0 ? "tra " : "";
    const suffix = diff < 0 ? " fa" : "";
    if (abs < 60) return `${prefix}${abs}s${suffix}`;
    if (abs < 3600) return `${prefix}${Math.round(abs / 60)} min${suffix}`;
    return `${prefix}${Math.round(abs / 3600)} h${suffix}`;
  } catch {
    return "—";
  }
}
function fmtTick(iso) {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}

/* ---------- KPI Card -------------------------------------------------- */
function KpiCard({ label, value, unit, icon: Icon, colorClass, hint, testid, children }) {
  return (
    <div
      className="ih-glass rounded-2xl p-5 relative overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
          <Icon className="h-3.5 w-3.5" />
          <span>{label}</span>
        </div>
      </div>
      <div className={`mt-3 text-4xl font-display font-semibold tabular-nums ${colorClass}`}>
        {value}
        {unit && <span className="text-lg font-normal text-muted-foreground ml-1">{unit}</span>}
      </div>
      {hint && <div className="text-[11px] text-muted-foreground mt-2">{hint}</div>}
      {children}
    </div>
  );
}

/* ---------- Progress bar --------------------------------------------- */
function Bar({ value, max = 100, color = "bg-primary" }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="mt-3 h-1.5 rounded-full bg-muted/50 overflow-hidden">
      <div
        className={`h-full ${color} transition-all duration-500`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ---------- Main component ------------------------------------------- */
export default function AdminHealth() {
  const [intervalSec, setIntervalSec] = useState(30);
  const [running, setRunning] = useState(true);
  const [latest, setLatest] = useState(null);
  const [memProfile, setMemProfile] = useState(null); // memory-profile ultimo snapshot
  const [memBaseline, setMemBaseline] = useState(() => {
    try {
      const raw = localStorage.getItem("ih_mem_baseline");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [buffer, setBuffer] = useState([]); // {ts, load, cpu, mem, mongo, rss, threads}
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [, tick] = useState(0); // forced tick per "ultimo update: Ns fa"
  const [errorStreak, setErrorStreak] = useState(0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const r = await api.get("/debug/health-metrics");
      const d = r.data;
      setLatest(d);
      setLastFetchAt(new Date());
      setErrorStreak(0);
      // Memory-profile ogni 2 polling (gc.get_objects è pesante, ~100ms)
      let mp = null;
      try {
        const rm = await api.get("/debug/memory-profile");
        mp = rm.data;
        setMemProfile(mp);
      } catch { /* best effort */ }
      setBuffer((prev) => {
        const next = [...prev, {
          ts: d.ts || new Date().toISOString(),
          load: d?.pod?.load_avg?.[0] ?? null,
          cpu: d?.pod?.cpu_pct ?? null,
          mem: d?.pod?.memory_pct ?? null,
          mongo: d?.db?.ping_ms ?? null,
          rss: mp?.process?.rss_mb ?? null,
          threads: mp?.process?.threads ?? null,
        }];
        return next.length > BUFFER_MAX ? next.slice(-BUFFER_MAX) : next;
      });
    } catch (e) {
      setErrorStreak((s) => {
        const nx = s + 1;
        if (nx === CONSECUTIVE_ERR_THRESHOLD) toast.error(`Health check fallito ${nx} volte: ${formatApiError(e)}`);
        return nx;
      });
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Polling loop (respects visibilityState)
  useEffect(() => {
    if (!running) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }
    fetchOnce();
    const schedule = () => {
      if (document.visibilityState !== "visible") return;
      fetchOnce();
    };
    timerRef.current = setInterval(schedule, intervalSec * 1000);
    const onVis = () => {
      if (document.visibilityState === "visible") fetchOnce();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [running, intervalSec, fetchOnce]);

  // Tick ogni secondo per aggiornare "ultimo update Xs fa"
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const load = latest?.pod?.load_avg?.[0];
  const cpu = latest?.pod?.cpu_pct;
  const mem = latest?.pod?.memory_pct;
  const mongo = latest?.db?.ping_ms;
  const cpuCount = latest?.pod?.cpu_count ?? 1;
  const memAvail = latest?.pod?.memory_available_mb;
  const memTotal = latest?.pod?.memory_total_mb;

  const stress = useMemo(() => {
    if (load == null) return null;
    if (load >= STRESS_CRIT) return "critical";
    if (load >= STRESS_WARN) return "warning";
    return null;
  }, [load]);

  const secondsSinceFetch = lastFetchAt ? Math.round((Date.now() - lastFetchAt.getTime()) / 1000) : null;

  const _triggerDownload = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadMemorySnapshot = async () => {
    try {
      const r = await api.get("/debug/memory-profile");
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      _triggerDownload(r.data, `memory-profile-${ts}.json`);
      toast.success("Snapshot scaricato");
    } catch (e) {
      toast.error(`Errore: ${formatApiError(e)}`);
    }
  };

  const captureOrDiffBaseline = async () => {
    try {
      const r = await api.get("/debug/memory-profile");
      const snap = r.data;
      if (!memBaseline) {
        localStorage.setItem("ih_mem_baseline", JSON.stringify(snap));
        setMemBaseline(snap);
        toast.success(`T0 salvato alle ${new Date().toLocaleTimeString("it-IT")}`);
        return;
      }
      const b = memBaseline;
      const t1 = snap;
      const typeMap = Object.fromEntries((b?.python?.top_30_types || []).map((t) => [t.type, t.count]));
      const typesDelta = (t1?.python?.top_30_types || [])
        .map((t) => ({ type: t.type, t0: typeMap[t.type] ?? 0, t1: t.count, delta: t.count - (typeMap[t.type] ?? 0) }))
        .filter((x) => Math.abs(x.delta) > 100)
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
        .slice(0, 20);
      const dSec = (new Date(t1.ts) - new Date(b.ts)) / 1000;
      const payload = {
        t0: { ts: b.ts, process: b.process, python: b.python, mongo_pool: b.mongo_pool },
        t1: { ts: t1.ts, process: t1.process, python: t1.python, mongo_pool: t1.mongo_pool },
        delta: {
          duration_seconds: dSec,
          rss_mb: +((t1.process?.rss_mb ?? 0) - (b.process?.rss_mb ?? 0)).toFixed(2),
          threads: (t1.process?.threads ?? 0) - (b.process?.threads ?? 0),
          fds: (t1.process?.fds ?? 0) - (b.process?.fds ?? 0),
          gc_objects_total: (t1.python?.gc_objects_total ?? 0) - (b.python?.gc_objects_total ?? 0),
          types_with_change_gt_100: typesDelta,
        },
      };
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      _triggerDownload(payload, `memory-delta-${ts}.json`);
      const leakHint = payload.delta.rss_mb > 100 || payload.delta.gc_objects_total > 10000
        ? ` · ⚠️ possibile leak: RSS +${payload.delta.rss_mb}MB, objects +${payload.delta.gc_objects_total.toLocaleString("it-IT")}`
        : " · nessun leak visibile";
      toast.success(`Delta T0→T1 (${Math.round(dSec / 60)} min) scaricato${leakHint}`);
      localStorage.removeItem("ih_mem_baseline");
      setMemBaseline(null);
    } catch (e) {
      toast.error(`Errore: ${formatApiError(e)}`);
    }
  };

  const resetBaseline = () => {
    localStorage.removeItem("ih_mem_baseline");
    setMemBaseline(null);
    toast.success("Snapshot T0 resettato");
  };


  const exportCsv = () => {
    if (buffer.length === 0) {
      toast.error("Nessun dato da esportare");
      return;
    }
    const header = "timestamp,load_avg_1m,cpu_pct,memory_pct,mongo_ping_ms,rss_mb,threads";
    const rows = buffer.map((p) => `${p.ts},${p.load ?? ""},${p.cpu ?? ""},${p.mem ?? ""},${p.mongo ?? ""},${p.rss ?? ""},${p.threads ?? ""}`);
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `health-metrics_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Export: ${buffer.length} punti dati`);
  };

  return (
    <div className="space-y-6" data-testid="admin-health-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-display font-semibold flex items-center gap-2">
            <HeartPulse className="h-7 w-7 text-primary" />
            Health Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Diagnostica real-time del backend. Polling ogni {intervalSec}s · Buffer trend ultimi {Math.round((buffer.length * intervalSec) / 60)} min
            {lastFetchAt && (
              <>
                {" "}· <span className="tabular-nums">Aggiornato {secondsSinceFetch}s fa</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            Intervallo
            <select
              value={intervalSec}
              onChange={(e) => setIntervalSec(parseInt(e.target.value))}
              className="ih-glass rounded-md px-2 py-1 text-xs ml-1"
              data-testid="admin-health-polling-interval-select"
            >
              {POLL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <button
            onClick={() => setRunning((r) => !r)}
            className={`ih-glass rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${running ? "text-emerald-500 hover:bg-emerald-500/10" : "text-amber-500 hover:bg-amber-500/10"}`}
            data-testid="admin-health-polling-toggle"
          >
            {running ? <><Pause className="h-3.5 w-3.5" /> Pausa</> : <><Play className="h-3.5 w-3.5" /> Riprendi</>}
          </button>
          <button
            onClick={() => fetchOnce()}
            className="ih-glass rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1 text-primary hover:bg-primary/10"
            data-testid="admin-health-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Ora
          </button>
          <button
            onClick={exportCsv}
            className="ih-glass rounded-md px-3 py-1.5 text-xs font-medium flex items-center gap-1 text-primary hover:bg-primary/10"
            data-testid="admin-health-export-csv-btn"
          >
            <Download className="h-3.5 w-3.5" /> Esporta CSV
          </button>
        </div>
      </div>

      {/* Stress banner */}
      {stress && (
        <div
          className={`rounded-xl border px-5 py-3 flex items-center gap-3 ${stress === "critical" ? "bg-rose-500/10 border-rose-500/30 text-rose-500" : "bg-amber-500/10 border-amber-500/30 text-amber-500"}`}
          data-testid="admin-health-stress-banner"
        >
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="text-sm font-medium">
            {stress === "critical"
              ? `🚨 Server saturato — load_avg ${load?.toFixed(2)} su ${cpuCount} CPU. Rischio timeout immediato per gli utenti.`
              : `⚠️ Server sotto stress — load_avg ${load?.toFixed(2)} su ${cpuCount} CPU. Monitorare.`}
          </div>
        </div>
      )}
      {errorStreak >= CONSECUTIVE_ERR_THRESHOLD && (
        <div className="rounded-xl border bg-rose-500/10 border-rose-500/30 text-rose-500 px-5 py-3 flex items-center gap-3" data-testid="admin-health-error-banner">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="text-sm font-medium">
            ⚠️ Impossibile raggiungere il server ({errorStreak} tentativi falliti)
          </div>
        </div>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Load Average (1m)"
          icon={Activity}
          value={load == null ? "—" : load.toFixed(2)}
          unit={`/ ${cpuCount}`}
          colorClass={pctColor(load, { greenMax: 1.5, amberMax: 3 })}
          hint={`CPU totali: ${cpuCount}`}
          testid="admin-health-kpi-load-avg"
        />
        <KpiCard
          label="CPU"
          icon={Cpu}
          value={cpu == null ? "—" : cpu.toFixed(1)}
          unit="%"
          colorClass={pctColor(cpu, { greenMax: 50, amberMax: 80 })}
          testid="admin-health-kpi-cpu"
        >
          <Bar value={cpu ?? 0} color={cpu > 80 ? "bg-rose-500" : cpu > 50 ? "bg-amber-500" : "bg-emerald-500"} />
        </KpiCard>
        <KpiCard
          label="Memoria"
          icon={MemoryStick}
          value={mem == null ? "—" : mem.toFixed(1)}
          unit="%"
          colorClass={pctColor(mem, { greenMax: 70, amberMax: 90 })}
          hint={memAvail && memTotal ? `${(memAvail / 1024).toFixed(1)}GB liberi / ${(memTotal / 1024).toFixed(1)}GB` : null}
          testid="admin-health-kpi-memory"
        >
          <Bar value={mem ?? 0} color={mem > 90 ? "bg-rose-500" : mem > 70 ? "bg-amber-500" : "bg-emerald-500"} />
        </KpiCard>
        <KpiCard
          label="Mongo Ping"
          icon={Database}
          value={mongo == null ? "—" : mongo.toFixed(0)}
          unit="ms"
          colorClass={pctColor(mongo, { greenMax: 50, amberMax: 200 })}
          hint={latest?.db?.server?.connections ? `${latest.db.server.connections.current ?? "?"} conn attive` : null}
          testid="admin-health-kpi-mongo-ping"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-chart-loadavg">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Load Average — trend {Math.round((buffer.length * intervalSec) / 60)} min
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={buffer} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="ts" tick={{ fontSize: 10 }} tickFormatter={fmtTick} minTickGap={30} />
              <YAxis domain={[0, (dataMax) => Math.max(10, Math.ceil(dataMax * 1.2))]} tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}
                labelFormatter={fmtTick}
                formatter={(v) => (v == null ? "—" : v.toFixed(2))}
              />
              <ReferenceLine y={STRESS_WARN} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "warn", fontSize: 10, fill: "#f59e0b", position: "insideLeft" }} />
              <ReferenceLine y={STRESS_CRIT} stroke="#e11d48" strokeDasharray="4 4" label={{ value: "crit", fontSize: 10, fill: "#e11d48", position: "insideLeft" }} />
              <Line type="monotone" dataKey="load" stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} name="load avg (1m)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-chart-cpumem">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <Cpu className="h-4 w-4 text-primary" />
            CPU & Memory — trend {Math.round((buffer.length * intervalSec) / 60)} min
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={buffer} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="ts" tick={{ fontSize: 10 }} tickFormatter={fmtTick} minTickGap={30} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}
                labelFormatter={fmtTick}
                formatter={(v) => (v == null ? "—" : `${v.toFixed(1)}%`)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="cpu" stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} name="CPU %" />
              <Line type="monotone" dataKey="mem" stroke="#a855f7" strokeWidth={2} dot={false} isAnimationActive={false} name="Memory %" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* v121 — Memory & Disk widgets */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-memory-detail">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <div className="text-sm font-medium flex items-center gap-2">
              <MemoryStick className="h-4 w-4 text-primary" />
              Memoria backend · dettaglio
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={downloadMemorySnapshot}
                className="ih-glass rounded-md px-2.5 py-1 text-[11px] font-medium flex items-center gap-1 text-primary hover:bg-primary/10 border border-primary/20 transition-colors"
                data-testid="admin-health-memory-snapshot-btn"
                title="Scarica il JSON corrente del memory-profile"
              >
                <Download className="h-3 w-3" /> Snapshot JSON
              </button>
              <button
                onClick={captureOrDiffBaseline}
                className={`ih-glass rounded-md px-2.5 py-1 text-[11px] font-medium flex items-center gap-1 border transition-colors ${memBaseline ? "text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/10" : "text-primary border-primary/20 hover:bg-primary/10"}`}
                data-testid="admin-health-memory-delta-btn"
                title={memBaseline ? "Scarica il delta T0→T1 e resetta" : "Salva baseline T0 ora"}
              >
                <Download className="h-3 w-3" />
                {memBaseline ? `Scarica delta (T0: ${new Date(memBaseline.ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })})` : "Salva T0 per delta"}
              </button>
              {memBaseline && (
                <button
                  onClick={resetBaseline}
                  className="text-[11px] text-muted-foreground hover:text-rose-500 px-1.5 py-1"
                  data-testid="admin-health-memory-reset-baseline-btn"
                  title="Reset snapshot comparativo"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border/40 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">RSS backend</div>
              <div className={`text-2xl font-semibold tabular-nums ${pctColor(memProfile?.process?.rss_mb, { greenMax: 500, amberMax: 1200 })}`}>
                {memProfile?.process?.rss_mb != null ? `${memProfile.process.rss_mb.toFixed(0)} MB` : "—"}
              </div>
            </div>
            <div className="rounded-lg border border-border/40 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Thread attivi</div>
              <div className={`text-2xl font-semibold tabular-nums ${pctColor(memProfile?.process?.threads, { greenMax: 25, amberMax: 50 })}`}>
                {memProfile?.process?.threads ?? "—"}
              </div>
            </div>
            <div className="rounded-lg border border-border/40 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">File descriptor</div>
              <div className={`text-2xl font-semibold tabular-nums ${pctColor(memProfile?.process?.fds, { greenMax: 100, amberMax: 500 })}`}>
                {memProfile?.process?.fds ?? "—"}
              </div>
            </div>
            <div className="rounded-lg border border-border/40 px-3 py-2">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">GC objects</div>
              <div className="text-2xl font-semibold tabular-nums text-foreground">
                {memProfile?.python?.gc_objects_total != null ? memProfile.python.gc_objects_total.toLocaleString("it-IT") : "—"}
              </div>
            </div>
          </div>
          {memProfile?.process?.uptime_seconds != null && (
            <div className="mt-3 text-[11px] text-muted-foreground">
              Uptime: {Math.round(memProfile.process.uptime_seconds / 60)} min · Pool Mongo max: {memProfile?.mongo_pool?.max_pool_size ?? "default"} · Threadpool max: {memProfile?.thread_pool?.max_workers ?? "default"}
            </div>
          )}
          {memProfile?.python?.top_30_types && (
            <details className="mt-3">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">Top 10 tipi Python</summary>
              <div className="mt-2 space-y-1 text-xs tabular-nums">
                {memProfile.python.top_30_types.slice(0, 10).map((t) => (
                  <div key={t.type} className="flex justify-between border-b border-border/30 py-0.5">
                    <span className="font-mono">{t.type}</span>
                    <span className="text-muted-foreground">{t.count.toLocaleString("it-IT")}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-disk-breakdown">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-primary" />
            Spazio disco · dove va
          </div>
          {latest?.pod?.disk_pct != null && (
            <div className="mb-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">Totale pod</span>
                <span className={`text-xl font-semibold tabular-nums ${pctColor(latest.pod.disk_pct, { greenMax: 70, amberMax: 90 })}`}>
                  {latest.pod.disk_pct}%
                </span>
              </div>
              <Bar value={latest.pod.disk_pct} color={latest.pod.disk_pct > 90 ? "bg-rose-500" : latest.pod.disk_pct > 70 ? "bg-amber-500" : "bg-emerald-500"} />
              <div className="text-[11px] text-muted-foreground mt-1">
                {latest.pod.disk_free_gb}GB liberi / {latest.pod.disk_total_gb}GB
              </div>
            </div>
          )}
          <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Top directory</div>
          <div className="space-y-1 text-xs tabular-nums">
            {(latest?.disk_breakdown?.top_dirs || []).map((d) => (
              <div key={d.path} className="flex justify-between border-b border-border/30 py-0.5">
                <span className="font-mono">{d.path}</span>
                <span className={d.mb > 1000 ? "text-rose-500 font-semibold" : d.mb > 300 ? "text-amber-500" : "text-muted-foreground"}>
                  {d.mb >= 1024 ? `${(d.mb / 1024).toFixed(1)} GB` : `${d.mb.toFixed(0)} MB`}
                </span>
              </div>
            ))}
            {!latest?.disk_breakdown?.top_dirs?.length && (
              <div className="text-muted-foreground text-xs">Nessun dato</div>
            )}
          </div>
          {latest?.disk_breakdown?.mongo_data && (
            <div className="mt-3 pt-2 border-t border-border/30 text-xs text-muted-foreground">
              MongoDB locale: <span className="font-mono tabular-nums text-foreground">{latest.disk_breakdown.mongo_data.mb} MB</span>
            </div>
          )}
          <details className="mt-3">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">Top 10 file log</summary>
            <div className="mt-2 space-y-0.5 text-[11px] tabular-nums max-h-48 overflow-y-auto">
              {(latest?.disk_breakdown?.top_log_files || []).map((f) => (
                <div key={f.path} className="flex justify-between gap-2 border-b border-border/30 py-0.5">
                  <span className="font-mono truncate" title={f.path}>{f.path.replace("/var/log/", "")}</span>
                  <span className="text-muted-foreground shrink-0">{f.mb} MB · {f.age_hours}h</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>

      {/* v121 — RSS memory trend chart */}
      <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-chart-rss">
        <div className="text-sm font-medium mb-3 flex items-center gap-2">
          <MemoryStick className="h-4 w-4 text-primary" />
          RSS Backend — trend {Math.round((buffer.length * intervalSec) / 60)} min
          <span className="text-xs text-muted-foreground font-normal ml-2">
            (crescita indica memory leak)
          </span>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={buffer} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="ts" tick={{ fontSize: 10 }} tickFormatter={fmtTick} minTickGap={30} />
            <YAxis domain={[0, (dataMax) => Math.max(500, Math.ceil(dataMax * 1.2))]} tick={{ fontSize: 10 }} unit=" MB" />
            <Tooltip
              contentStyle={{ backgroundColor: "rgba(255,255,255,0.95)", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11 }}
              labelFormatter={fmtTick}
              formatter={(v) => (v == null ? "—" : `${v.toFixed(1)} MB`)}
            />
            <ReferenceLine y={1500} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "warn", fontSize: 10, fill: "#f59e0b", position: "insideLeft" }} />
            <ReferenceLine y={2500} stroke="#e11d48" strokeDasharray="4 4" label={{ value: "crit", fontSize: 10, fill: "#e11d48", position: "insideLeft" }} />
            <Line type="monotone" dataKey="rss" stroke="#a855f7" strokeWidth={2} dot={false} isAnimationActive={false} name="RSS (MB)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Scheduler jobs */}
      <div className="ih-glass rounded-2xl p-5" data-testid="admin-health-scheduler-table">
        <div className="text-sm font-medium mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" />
          Scheduler Jobs {latest?.scheduler?.status && (
            <span className="text-xs text-muted-foreground">
              · stato: <span className={latest.scheduler.status === "running" ? "text-emerald-500" : "text-amber-500"}>{latest.scheduler.status}</span>
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border/40">
                <th className="py-2 pr-3">Job</th>
                <th className="py-2 pr-3">Trigger</th>
                <th className="py-2 pr-3">Prossima esecuzione</th>
                <th className="py-2 pr-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(latest?.scheduler?.jobs || []).map((j) => {
                const nextTs = j.next_run && j.next_run !== "None" ? new Date(j.next_run).getTime() : null;
                const now = Date.now();
                const overdue = nextTs != null && nextTs < now - 30_000; // >30s in the past
                return (
                  <tr
                    key={j.id}
                    className="border-b border-border/40"
                    data-testid={`admin-health-scheduler-row-${j.id}`}
                  >
                    <td className="py-2 pr-3 font-medium">{j.name || j.id}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground font-mono">
                      {(j.trigger || "").replace(/<|>|CronTrigger\s*/g, "").slice(0, 80)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">
                      <div className={overdue ? "text-rose-500 font-medium" : ""}>{fmtRelative(j.next_run)}</div>
                      <div className="text-[10px] text-muted-foreground">{j.next_run && j.next_run !== "None" ? new Date(j.next_run).toLocaleString("it-IT") : "—"}</div>
                    </td>
                    <td className="py-2 pr-3">
                      {overdue ? (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-500">
                          <AlertTriangle className="h-3 w-3" /> overdue
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                          <CheckCircle2 className="h-3 w-3" /> ok
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {(!latest?.scheduler?.jobs || latest.scheduler.jobs.length === 0) && (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground text-xs">Nessun job scheduler</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mongo collections counts (compact) */}
      {latest?.db?.counts && (
        <div className="ih-glass rounded-2xl p-5">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <Database className="h-4 w-4 text-primary" />
            MongoDB · collection counts
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(latest.db.counts).map(([k, v]) => (
              <div key={k} className="rounded-lg border border-border/40 px-3 py-2">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide truncate" title={k}>{k}</div>
                <div className="text-lg font-semibold tabular-nums">{v == null ? "—" : v.toLocaleString("it-IT")}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground pt-3 border-t border-border/30">
        <Link to="/configurazione/scheduler" className="underline hover:text-primary">Dettaglio scheduler</Link>
        {" · "}
        <Link to="/configurazione/audit-log" className="underline hover:text-primary">Registro attività</Link>
        {" · Endpoint sorgente: "}
        <code className="font-mono text-[10px]">GET /api/debug/health-metrics</code>
      </div>
    </div>
  );
}
